import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { useFeatureFlags } from '@/contexts';
import type { FeatureFlagKey } from '@/contexts/feature-flags-context/types';

import AppLoading from '../AppLoading/AppLoading';

type Props = {
  feature: FeatureFlagKey;
  redirectTo?: string;
  children?: ReactNode;
};

const FeatureRoute = ({ feature, children, redirectTo = '/' }: Props) => {
  const { isEnabled, identificationStatus } = useFeatureFlags();

  // Until LaunchDarkly has evaluated for the signed-in user and scope the flag
  // still carries an earlier context's value — at boot, the anonymous one,
  // which is false for anything targeted at an allowlist or a partial rollout.
  // Redirecting on it turns a direct navigation — a refresh, a bookmark, a
  // pasted link — into a bounce to Home for the very users the flag is on for,
  // and the redirect is never revisited once the real value lands.
  if (identificationStatus === 'pending') {
    return <AppLoading />;
  }

  // Fails closed when the evaluation never arrived. The flags still in hand
  // describe an earlier context, so an `isEnabled` of true there is that
  // context's answer, not this one's — and this gate stands in for a
  // permission the product does not have yet.
  if (identificationStatus !== 'evaluated' || !isEnabled(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default FeatureRoute;
